/* eslint-disable import/prefer-default-export, consistent-return */
import { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { translate } from 'translations';
import { selectIsLeanUser, selectToken, selectTenantName } from '@redux/slices/userSlice';
import { ContentCardProps, Loader } from 'learning-panel-components';
import CardsGalleryContainer from '@components/cardsGallery/cardsGalleryContainer';
import LearningPathBanner from '@components/learningPath/learningPathBanner/learningPathBanner';
import { useRecentlyAdded } from '@queries/core/homepage';
import convertCourseToContentCard from '@services/convertCourseToContentCard';
import convertLessonToContentCard from '@services/convertLessonToContentCard';
import convertLearningPathToContentCard from '@services/convertLearningPathToContentCard';
import convertSkillTreeToContentCard from '@services/convertSkillTreeToContentCard';
import convertVideosToContentCard from '@services/convertVideosToContentCard';
import SkillTreeBanner from '@components/skillTree/skillTreeBanner/skillTreeBanner';
import convertCourseToBanner from '@services/convertCourseToBanner';
import convertLearningPathToBanner from '@services/convertLearningPathToBanner';
import convertSkillTreeToBanner from '@services/convertSkillTreeToBanner';
import CourseBanner from '@components/learn/courseBanner/courseBanner';
import VideoTutorialBanner from '@components/home/homeTabs/videoTutorialsPage/videoTutorialsBanner/videoTutorialsBanner';
import convertVideosToBanner from '@services/convertVideosToBanner';
import { progressOrder, filterHiddenLearningPaths } from '@components/learningPath/utils';
import { coreService } from 'network-utils';
import styles from './recentlyAdded.module.scss';

const RecentlyAddedContainer: FC<RecentlyAddedProps> = (props: RecentlyAddedProps) => {
    const { setCertificateCourseUuid, setIsCustomCourse, setVideoTutorialUuid, onContactUsClick, fullWidth } = props;
    const token = useSelector(selectToken);
    const isLeanUser = useSelector(selectIsLeanUser);
    const tenantName = useSelector(selectTenantName);
    const [coursesAndLessons, setCoursesAndLessons] = useState<ContentCardProps[]>([]);
    const [skillTreeAndLearningPaths, setSkillTreeAndLearningPaths] = useState<ContentCardProps[]>([]);
    const [videos, setVideos] = useState<ContentCardProps[]>([]);
    const [maxDataContent, setMaxDataContent] = useState<{ content: any; type: string }>({ content: {}, type: '' });
    const { data: recentlyAddedData, isLoading: recentlyAddedLoading } = useRecentlyAdded(token);

    useEffect(() => {
        if (!recentlyAddedData) return;
        if ((recentlyAddedData as any).success === false) return;

        let maxDate = new Date(0);
        const tempCourses = recentlyAddedData.courses.map((course: coreService.Course) => {
            const contentCard = convertCourseToContentCard(
                course,
                isLeanUser,
                setCertificateCourseUuid,
                setIsCustomCourse
            );

            if (course.publishedAt) {
                const publishedAtDate = new Date(course.publishedAt);
                if (publishedAtDate > maxDate) {
                    if (isLeanUser && course.activeOnLean) {
                        maxDate = publishedAtDate;
                        setMaxDataContent({ content: course, type: 'course' });
                    } else {
                        maxDate = publishedAtDate;
                        setMaxDataContent({ content: course, type: 'course' });
                    }
                }
            }

            return contentCard;
        });
        const tempLessons = recentlyAddedData.lessons.map((lesson: any) => {
            const contentCard = convertLessonToContentCard(lesson, isLeanUser, onContactUsClick);
            return contentCard;
        });

        const tempSkillTree = recentlyAddedData.skillTrees.map((skillTree: any) => {
            const contentCard = convertSkillTreeToContentCard(skillTree);

            if (skillTree.createdAt) {
                const createdAtDate = new Date(skillTree.createdAt);
                if (createdAtDate > maxDate) {
                    maxDate = createdAtDate;
                    setMaxDataContent({ content: skillTree, type: 'skillTree' });
                }
            }

            return contentCard;
        });
        const visibleLearningPaths = filterHiddenLearningPaths<coreService.LearningPath>(
            recentlyAddedData.learningPaths,
            tenantName
        );
        const tempLearningPaths = visibleLearningPaths.map((learningPath: any) => {
            const contentCard = convertLearningPathToContentCard(learningPath);
            if (learningPath.createdAt) {
                const createdAtDate = new Date(learningPath.createdAt);
                if (createdAtDate > maxDate) {
                    maxDate = createdAtDate;
                    setMaxDataContent({ content: learningPath, type: 'learningPath' });
                }
            }

            return contentCard;
        });

        const tempVideo = recentlyAddedData.videoTutorials.map((video: any) => {
            const contentCard = convertVideosToContentCard(video, setVideoTutorialUuid);
            if (video.createdAt) {
                const createdAtDate = new Date(video.createdAt);
                if (createdAtDate > maxDate) {
                    maxDate = createdAtDate;
                    setMaxDataContent({ content: video, type: 'video' });
                }
            }
            return contentCard;
        });

        const lessonsAndCoursesSorted = [...tempCourses, ...tempLessons].sort((a, b) => {
            const indexA = progressOrder.indexOf(a.status);
            const indexB = progressOrder.indexOf(b.status);
            return indexA - indexB;
        });
        setCoursesAndLessons(lessonsAndCoursesSorted);

        const skillTreeAndLearningPaths = [...tempSkillTree, ...tempLearningPaths].sort((a, b) => {
            const indexA = progressOrder.indexOf(a.status);
            const indexB = progressOrder.indexOf(b.status);
            return indexA - indexB;
        });
        setSkillTreeAndLearningPaths(skillTreeAndLearningPaths);
        setVideos(tempVideo);
    }, [recentlyAddedData, tenantName]);

    const getBanner = () => {
        if (maxDataContent.type === 'course') {
            const courseBanner = convertCourseToBanner(maxDataContent.content);
            return <CourseBanner {...courseBanner} />;
        }
        if (maxDataContent.type === 'skillTree') {
            const skillTreeBanner = convertSkillTreeToBanner(maxDataContent.content);
            return <SkillTreeBanner {...skillTreeBanner} />;
        }
        if (maxDataContent.type === 'learningPath') {
            const learningPathBanner = convertLearningPathToBanner(maxDataContent.content);
            return <LearningPathBanner {...learningPathBanner} />;
        }
        if (maxDataContent.type === 'video') {
            const videoBanner = convertVideosToBanner(maxDataContent.content, setVideoTutorialUuid);
            return <VideoTutorialBanner {...videoBanner} />;
        }
    };

    return (
        <div id="career-page" className={styles['recently-added-wrapper']}>
            {recentlyAddedLoading ? (
                <Loader showBackground={false} />
            ) : (
                <>
                    {getBanner()}
                    <div className={fullWidth ? styles.container : styles['no-full-container']}>
                        <CardsGalleryContainer
                            rows={2}
                            columns={2}
                            noFullWidthColumn={3}
                            data={coursesAndLessons}
                            title={translate('Lessons&Courses')}
                            fullWidth={fullWidth}
                            infoDescription={translate('CoursesAndLessonsWe’veAddedLately')}
                        />
                        <div className={styles.space}></div>
                        <CardsGalleryContainer
                            rows={2}
                            columns={2}
                            noFullWidthColumn={3}
                            data={skillTreeAndLearningPaths}
                            title={translate('SkillTree&LearningPaths')}
                            fullWidth={fullWidth}
                            infoDescription={translate('SkillTreesAndLearningPathsWe’veAddedLately')}
                        />
                    </div>
                    <div className={styles.container}>
                        <CardsGalleryContainer
                            rows={2}
                            noFullWidthColumn={2}
                            columns={2}
                            data={videos}
                            fullWidth={fullWidth}
                            title={translate('Videos')}
                            infoDescription={translate('VideosWe’veAddedLately')}
                        />
                    </div>
                </>
            )}
        </div>
    );
};

interface RecentlyAddedProps {
    setCertificateCourseUuid: Function;
    setIsCustomCourse: Function;
    setVideoTutorialUuid: Function;
    onContactUsClick: Function;
    fullWidth?: boolean;
}

export default RecentlyAddedContainer;
